/** API: `male`, `female`, or `other`. */
export type MembershipGender = 'male' | 'female' | 'other';

/** Church department/ministry assignment (dropdown on Add/Edit membership). */
export const MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS = [
  'Music/Audio Team',
  'Mens Team',
  'Womens Team',
  'Youth',
  'Children',
  'Usherette Team',
  'Maintenance and Staff',
] as const;

/** URL / UI tab keys for Church Reports → Departmental (same order as options above). */
export const DEPARTMENT_MINISTRY_REPORT_TAB_KEYS = [
  'musicAudio',
  'mens',
  'womens',
  'youth',
  'children',
  'usherette',
  'maintenanceStaff',
] as const;

export type DepartmentMinistryReportTabKey =
  (typeof DEPARTMENT_MINISTRY_REPORT_TAB_KEYS)[number];

export function departmentMinistryLabelForTabKey(
  key: DepartmentMinistryReportTabKey,
): string {
  const index = DEPARTMENT_MINISTRY_REPORT_TAB_KEYS.indexOf(key);
  if (index < 0) return MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS[0];
  return MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS[index];
}

export function isDepartmentMinistryReportTabKey(
  value: string | null | undefined,
): value is DepartmentMinistryReportTabKey {
  return (
    !!value &&
    (DEPARTMENT_MINISTRY_REPORT_TAB_KEYS as readonly string[]).includes(value)
  );
}

export interface Membership {
  id: string;
  userId?: string | null;
  email?: string | null;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  dateOfBirth?: string | null;
  gender?: MembershipGender | null;
  civilStatus?: string | null;
  departmentMinistry?: string | null;
  nationality?: string | null;
  address?: string | null;
  contactNumber?: string | null;
  occupation?: string | null;
  employer?: string | null;
  membershipDate: string;
  membershipType?: string | null;
  isSgl?: boolean | null;
  isSgm?: boolean | null;
  willingToWorkInTupad?: boolean | null;
  status?: 'active' | 'inactive' | 'suspended';
  /** `pending` until the invitee accepts or declines (email invitations only). */
  invitationStatus?: string | null;
  notes?: string | null;
  emergencyContactName?: string | null;
  emergencyContactNumber?: string | null;
  company?: { id: string; name?: string };
  isVoided?: boolean | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMembershipRequest {
  userId?: string;
  email?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth?: string;
  gender?: MembershipGender;
  civilStatus?: 'single' | 'married' | 'widowed' | 'separated' | 'divorced';
  departmentMinistry?: string;
  nationality?: string;
  address?: string;
  contactNumber?: string;
  occupation?: string;
  employer?: string;
  membershipDate: string;
  membershipType?: string;
  isSgl?: boolean;
  isSgm?: boolean;
  willingToWorkInTupad?: boolean;
  status?: 'active' | 'inactive' | 'suspended';
  notes?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
}

/**
 * PATCH body: all fields optional; only provided fields are updated.
 * To clear `email`, send `""` (empty string).
 * Dates must be ISO 8601. If `firstName` or `lastName` is sent, it must be non-empty after trim.
 */
export interface UpdateMembershipRequest {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  dateOfBirth?: string;
  gender?: MembershipGender;
  civilStatus?: 'single' | 'married' | 'widowed' | 'separated' | 'divorced';
  departmentMinistry?: string;
  nationality?: string;
  address?: string;
  contactNumber?: string;
  email?: string;
  occupation?: string;
  employer?: string;
  membershipDate?: string;
  membershipType?: string;
  isSgl?: boolean;
  isSgm?: boolean;
  willingToWorkInTupad?: boolean;
  status?: 'active' | 'inactive' | 'suspended';
  notes?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
}

/** Aggregated statistics for active (non-voided) memberships. */
export interface MembershipStatistics {
  totalMembers: number;
  byStatus: Record<string, number>;
  byMembershipType: Record<string, number>;
  byGender: Record<string, number>;
  byCivilStatus: Record<string, number>;
  byAgeGroup: Record<string, number>;
  newMembersThisMonth: number;
  newMembersThisYear: number;
}
